/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { FontIcon } from '@fluentui/react/lib/Icon';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import React from 'react';
import { LuisText } from 'src/core/components/LuisText';
import { getScopedTheme } from 'src/core/themes/luisStylist';

export type CluEntityCapabilityCollapsableCardProps = {
    title: string;
    count?: number;
    onRenderCount?: () => string;
    isCapabilityOpen?: boolean;
    children: JSX.Element;
    farComponent?: JSX.Element;
};

const scopedTheme = getScopedTheme('CluEntityCapabilityCollapsableCard');
const headerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { borderBottom: `${scopedTheme.borderColor} solid 1px` }
});

/**
 * A collapsable wrapper for an  entity capability.
 */
export const CluEntityCapabilityCollapsableCard = (props: CluEntityCapabilityCollapsableCardProps) => {
    const { title, count, onRenderCount, isCapabilityOpen = false, farComponent } = props;

    const [isOpen, setIsOpen] = React.useState<boolean>(isCapabilityOpen);

    return (
        <Stack tokens={{ childrenGap: 12, padding: 10 }}>
            <Stack horizontal horizontalAlign="space-between" tokens={{ padding: '10px 0' }} styles={headerStyles}>
                <Stack horizontal tokens={{ childrenGap: 22 }}>
                    <FontIcon
                        tabIndex={0}
                        role="menuitem"
                        aria-label={title}
                        aria-expanded={isOpen}
                        aria-hidden={false}
                        style={{ cursor: 'pointer' }}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                setIsOpen(!isOpen);
                            }
                        }}
                        onClick={() => setIsOpen(!isOpen)}
                        iconName={isOpen ? 'ChevronDown' : 'ChevronRight'}
                    />
                    <LuisText fontWeight="bold">
                        {title}&nbsp;
                        {(count || onRenderCount) && <LuisText fontWeight="bold">({count ?? onRenderCount?.()})</LuisText>}
                    </LuisText>
                </Stack>
                {farComponent}
            </Stack>
            {isOpen && props.children}
        </Stack>
    );
};
